import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Keeps scroll position sane across client-side navigations.
 *
 * React Router deliberately does not manage scroll. Without this, navigating
 * from the bottom of a long page to a new route leaves the visitor halfway
 * down the new page. When the target URL carries a #hash -- which every
 * "see the details" link and the header's action both use -- the element with
 * that id is scrolled to instead, and focused so a keyboard user arrives with
 * the context rather than at the top.
 *
 * The delay before measuring is a double `requestAnimationFrame`: a lazy-loaded
 * route has not rendered its content on the first frame after navigation, and
 * looking too early finds nothing to scroll to.
 */
export const ScrollManager = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0 });
      return undefined;
    }

    const id = hash.replace('#', '');
    let secondFrame = 0;

    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const target = document.getElementById(id);

        if (!target) {
          window.scrollTo({ top: 0, left: 0 });
          return;
        }

        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Focus without a second visual jump, so the keyboard position follows
        // the scroll position.
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [pathname, hash]);

  return null;
};

export default ScrollManager;
